/**
 * StaticRoutePolyline — a lightweight, no-network route renderer.
 *
 * Used for background (non-focused) technician routes on the board map. Instead
 * of spinning up a live Mapbox Directions request + DOM markers per route (see
 * RoutingControl), it draws the route's *stored* `route_geometry` as a single
 * polyline plus canvas-rendered circle markers. Routes without stored geometry
 * fall back to a straight line through their stops.
 *
 * This keeps large boards (many technicians × many stops) responsive: at most
 * the focused route runs live directions; everyone else is static paint.
 */
import React, { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import type { BoardRoute } from '../utils/routeBoardMapping';

interface StaticRoutePolylineProps {
  route: BoardRoute;
  baseLocation: { lat: number; lng: number };
  color: string;
  /** Dim this route (another technician is focused). */
  dimmed?: boolean;
}

/** Build [lat,lng] path points: stored geometry if present, else straight line. */
function buildLatLngs(
  route: BoardRoute,
  baseLocation: { lat: number; lng: number }
): L.LatLngExpression[] {
  const geometry = route.routeGeometry;
  if (geometry && geometry.coordinates.length > 1) {
    // Stored geometry is [lng, lat]; Leaflet wants [lat, lng].
    return geometry.coordinates.map(([lng, lat]) => [lat, lng] as [number, number]);
  }
  // Fallback: straight line from base through stops in order.
  const ordered = [...route.stops].sort((a, b) => (a.stop_order || 0) - (b.stop_order || 0));
  return [
    [baseLocation.lat, baseLocation.lng] as [number, number],
    ...ordered
      .filter(s => s.dealer.latitude && s.dealer.longitude)
      .map(s => [s.dealer.latitude, s.dealer.longitude] as [number, number])
  ];
}

const StaticRoutePolylineComponent: React.FC<StaticRoutePolylineProps> = ({
  route,
  baseLocation,
  color,
  dimmed
}) => {
  const map = useMap();

  useEffect(() => {
    if (!map || route.stops.length === 0) return;

    // One canvas renderer per route layer keeps marker drawing off the DOM.
    const renderer = L.canvas({ padding: 0.5 });
    const layer = L.layerGroup();

    const pathOpacity = dimmed ? 0.35 : 0.9;
    const markerOpacity = dimmed ? 0.4 : 1;

    // Route path
    const latlngs = buildLatLngs(route, baseLocation);
    if (latlngs.length > 1) {
      L.polyline(latlngs, {
        color,
        weight: 4,
        opacity: pathOpacity,
        renderer
      }).addTo(layer);
    }

    // Home marker (start location)
    L.circleMarker([baseLocation.lat, baseLocation.lng], {
      radius: 5,
      color: '#ffffff',
      weight: 2,
      fillColor: '#44403c',
      fillOpacity: markerOpacity,
      opacity: markerOpacity,
      renderer
    }).addTo(layer);

    // Stop markers (lightweight canvas dots, no tooltips)
    [...route.stops]
      .sort((a, b) => (a.stop_order || 0) - (b.stop_order || 0))
      .forEach(stop => {
        const { latitude, longitude } = stop.dealer;
        if (!latitude || !longitude) return;
        L.circleMarker([latitude, longitude], {
          radius: 5,
          color: '#ffffff',
          weight: 2,
          fillColor: color,
          fillOpacity: markerOpacity,
          opacity: markerOpacity,
          renderer
        }).addTo(layer);
      });

    layer.addTo(map);
    return () => {
      map.removeLayer(layer);
    };
  }, [map, route, baseLocation, color, dimmed]);

  return null;
};

export const StaticRoutePolyline = React.memo(StaticRoutePolylineComponent);

export default StaticRoutePolyline;
